import { loadStripe } from "@stripe/stripe-js";
import axios, { AxiosError } from "axios";
import { FormEvent, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAgencyAuth } from "../../context/AgencyAuthContext";
import bgImg from "../../pnai-admin-bg.jpg";
import Logo from "../common/Logo";
import Button from "../common/Button";
import Input from "../common/Input";
import Loading from "../common/Loading";
import { Country, State } from "country-state-city";
import { trackCompleteRegistration } from "../../utilities/fbpixel";

// Initialize Stripe
const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLIC_KEY ||
    "pk_live_51PydONG4bf6K0RLmdfEJzwpzVpDCNNdt0HjC5sn5QokyLJij6TflI306Xoi69SSqHORZ9cPnt3xw2leQx9rrYb0B00nka5yr7n"
);

// Agency evaluation codes that grant an evaluation period at signup.
// Display only — the server validates the code independently
// (AGENCY_EVALUATION_CODES in authRouter.ts).
const AGENCY_EVALUATION_CODES: Record<string, { seats: number; days: number }> =
  {
    "US-ARMY-26": { seats: 15, days: 45 },
  };

// Add these utility functions at the top level
const getPriceId = (searchParams: URLSearchParams) =>
  searchParams.get("priceId");
const getAgencyEvaluationCode = (searchParams: URLSearchParams) => {
  const code = searchParams.get("agencyEvaluationCode")?.trim().toUpperCase();
  return code && AGENCY_EVALUATION_CODES[code] ? code : null;
};
const appendSearchParams = (path: string, searchParams: URLSearchParams) =>
  `${path}${searchParams.size ? `?${searchParams.toString()}` : ""}`;

function App() {
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [department, setDepartment] = useState<string>("");
  const [address, setAddress] = useState<string>("");
  const [city, setCity] = useState<string>("");
  const [state, setState] = useState<string>("");
  const [zip, setZip] = useState<string>("");
  const [swornOfficers, setSwornOfficers] = useState<string>("");
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [errorState, setErrorState] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated, setIsAuthenticated, setAgency } = useAgencyAuth();
  const [searchParams] = useSearchParams();
  const agencyEvaluationCode = getAgencyEvaluationCode(searchParams);
  const evaluation = agencyEvaluationCode
    ? AGENCY_EVALUATION_CODES[agencyEvaluationCode]
    : null;
  // Staff-sent trial invitation: the Trial Requirements page forwards here
  // with ?trialInvite=<token>. The server grants the seats and expiry; this
  // only displays them and pins the email to the invited address.
  const trialInviteToken = searchParams.get("trialInvite");
  const [trialInvite, setTrialInvite] = useState<{
    email: string;
    officerSeats: number;
    investigatorSeats: number;
    trialDays: number;
  } | null>(null);
  const [trialInviteError, setTrialInviteError] = useState<string | null>(null);
  const [states, setStates] = useState<any[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<any>(
    Country.getCountryByCode("US")
  );

  useEffect(() => {
    if (!trialInviteToken) return;
    let cancelled = false;
    axios
      .get("/api/agency/trial-invitations/validate", {
        params: { token: trialInviteToken },
      })
      .then((res) => {
        if (cancelled) return;
        setTrialInvite(res.data);
        setEmail(res.data.email);
      })
      .catch((err) => {
        if (cancelled) return;
        setTrialInviteError(
          err?.response?.data?.error || "We could not open this invitation."
        );
      });
    return () => {
      cancelled = true;
    };
  }, [trialInviteToken]);

  useEffect(() => {
    if (selectedCountry) {
      const countryStates = State.getStatesOfCountry(selectedCountry.isoCode);
      setStates(countryStates);
      if (countryStates.length > 0) {
        setState(countryStates[0].isoCode);
      }
    }
  }, [selectedCountry]);

  useEffect(() => {
    if (isAuthenticated && !getPriceId(searchParams)) {
      setLoading(true);
      navigate("/agency/dashboard");
    }
  }, [navigate, isAuthenticated, searchParams]);

  const validateForm = () => {
    let newErrors: {
      email?: string;
      password?: string;
      confirmPassword?: string;
      department?: string;
      address?: string;
      city?: string;
      state?: string;
      zip?: string;
      phone?: string;
      swornOfficers?: string;
    } = {};

    // Department validation
    if (!department) {
      newErrors.department = "Department is required";
    }

    // Email validation
    if (!email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Email is invalid";
    }

    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{12,}$/;

    // Password validation
    if (!password) {
      newErrors.password = "Password is required";
    } else if (!passwordRegex.test(password)) {
      newErrors.password =
        "Password must be at least 12 characters long and include a combination of uppercase and lowercase letters, numbers, and symbols";
    }

    // Confirm password validation
    if (!confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    // Address validation
    if (!address.trim()) {
      newErrors.address = "Address is required";
    }

    // City validation
    if (!city.trim()) {
      newErrors.city = "City is required";
    }

    // State validation
    if (!state.trim()) {
      newErrors.state = "State is required";
    }

    // ZIP validation
    const zipRegex = /^\d{5}(-\d{4})?$/;
    if (!zip.trim()) {
      newErrors.zip = "ZIP code is required";
    } else if (!zipRegex.test(zip)) {
      newErrors.zip = "Invalid ZIP code format";
    }

    // Sworn officers validation
    if (!swornOfficers.trim()) {
      newErrors.swornOfficers = "Number of sworn officers is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      setLoading(true);
      try {
        const response = await axios.post("/api/auth/agency/signup", {
          email,
          password,
          name: department,
          address,
          city,
          state,
          zip,
          country: "US",
          sworn_officers: parseInt(swornOfficers),
          agencyEvaluationCode,
          ...(trialInviteToken && { trialInviteToken }),
        });

        if (response.data.agency) {
          // Agency account created server-side (HTTP 201) — fire the Meta
          // Pixel signup conversion once.
          trackCompleteRegistration();
          setIsAuthenticated(true);
          setAgency(response.data.agency);
          navigate("/agency/dashboard");
        }
      } catch (error) {
        if (axios.isAxiosError(error)) {
          setErrorState(true);
          const axiosError = error as AxiosError<{ error: string }>;
          if (axiosError.response) {
            setErrors({
              overall:
                axiosError.response.data.error || "An unknown error occurred",
            });
          }
        }
        setLoading(false);
      }
    } else {
      setErrorState(true);
    }
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2">
      <div
        className="hidden md:flex relative items-center justify-center bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${bgImg})` }}
      >
        <div className="absolute inset-0 bg-blue-50/70"></div>
        <div className="w-3/5 z-10">
          <Logo className="w-full h-auto" />
        </div>
      </div>
      <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-base-200">
        <div className="bg-base-100 p-8 rounded-xl shadow-md w-full max-w-sm">
          <h1 className="text-3xl mb-2 font-semibold">Agency Signup</h1>
          <p className="mb-3 text-xs text-base-content/60">
            Sign up for an account to purchase multiple seats.
          </p>

          {trialInviteError && (
            <div className="mb-4 rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/40 p-3">
              <p className="text-sm font-semibold text-red-700 dark:text-red-400">
                {trialInviteError}
              </p>
            </div>
          )}

          {trialInvite && (
            <div className="mb-4 rounded-md border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/40 p-3">
              <p className="text-sm font-semibold text-blue-800 dark:text-blue-200">
                Your agency is invited to evaluate Police Narratives AI
              </p>
              <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                Complete signup to begin your {trialInvite.trialDays}-day
                evaluation period.{" "}
                {trialInvite.officerSeats} officer{" "}
                {trialInvite.officerSeats === 1 ? "seat" : "seats"}
                {trialInvite.investigatorSeats > 0
                  ? ` and ${trialInvite.investigatorSeats} investigator ${
                      trialInvite.investigatorSeats === 1 ? "seat" : "seats"
                    }`
                  : ""}{" "}
                will be provisioned for your department.
              </p>
            </div>
          )}

          {!trialInvite && evaluation && (
            <div className="mb-4 rounded-md border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/40 p-3">
              <p className="text-sm font-semibold text-blue-800 dark:text-blue-200">
                Your agency is invited to evaluate Police Narratives AI
              </p>
              <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                Complete signup to begin your {evaluation.days}-day evaluation
                period. {evaluation.seats} seats will be provisioned for your
                department.
              </p>
            </div>
          )}

          <form className="space-y-4" onSubmit={handleSubmit}>
            <div>
              <p className="font-semibold pb-1 text-sm">Department</p>
              <Input
                type="text"
                id="department"
                value={department}
                placeholder="Full Department Name"
                onChange={(e) => {
                  errorState && setErrorState(false);
                  setDepartment(e.target.value);
                }}
                className={`input w-full ${errorState && "border-red-500"}`}
              />
              {errors.department && (
                <p className="mt-1 text-xs text-red-500">{errors.department}</p>
              )}
            </div>
            <div>
              <p className="font-semibold pb-1 text-sm">Sworn Officers</p>
              <Input
                type="number"
                value={swornOfficers}
                placeholder="# of sworn officers"
                min="0"
                onChange={(e) => {
                  errorState && setErrorState(false);
                  setSwornOfficers(e.target.value.replace(/\D/g, ""));
                }}
                onKeyDown={(e) => {
                  if (["e", "E", "+", "-", "."].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                className={`input w-full ${
                  errorState && errors.swornOfficers && "border-red-500"
                }`}
              />
              {errors.swornOfficers && (
                <p className="mt-1 text-xs text-red-500">
                  {errors.swornOfficers}
                </p>
              )}
            </div>
            <div>
              <p className="font-semibold pb-1 text-sm">Email</p>
              <Input
                type="email"
                id="email"
                value={email}
                placeholder="Email Address"
                disabled={!!trialInvite}
                onChange={(e) => {
                  errorState && setErrorState(false);
                  setEmail(e.target.value);
                }}
                className={`input w-full ${errorState && "border-red-500"}`}
              />
              {errors.email && (
                <p className="mt-1 text-xs text-red-500">{errors.email}</p>
              )}
            </div>
            <div>
              <p className="font-semibold pb-1 text-sm">Password</p>
              <Input
                type="password"
                id="password"
                value={password}
                placeholder="Password"
                onChange={(e) => {
                  errorState && setErrorState(false);
                  setPassword(e.target.value);
                }}
                className={`input w-full max-w-xs ${
                  errorState && "border-red-500"
                }`}
              />
              {errors.password && errorState && (
                <p className="mt-1 text-xs text-red-500">{errors.password}</p>
              )}
            </div>
            <div>
              <p className="font-semibold pb-1 text-sm">Confirm Password</p>
              <Input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                placeholder="Confirm password"
                onChange={(e) => {
                  errorState && setErrorState(false);
                  setConfirmPassword(e.target.value);
                }}
                className={`input w-full max-w-xs ${
                  errorState && "border-red-500"
                }`}
              />
              {errors.confirmPassword && errorState && (
                <p className="mt-1 text-xs text-red-500">
                  {errors.confirmPassword}
                </p>
              )}
              {errors.overall && errorState && (
                <p className="mt-1 text-xs text-red-500">{errors.overall}</p>
              )}
            </div>
            <div>
              <p className="font-semibold pb-1 text-sm">Agency Address</p>
              <Input
                type="text"
                value={address}
                placeholder="Street Address"
                onChange={(e) => {
                  errorState && setErrorState(false);
                  setAddress(e.target.value);
                }}
                className={`input w-full ${
                  errorState && errors.address && "border-red-500"
                }`}
              />
              {errors.address && (
                <p className="mt-1 text-xs text-red-500">{errors.address}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="font-semibold pb-1 text-sm">City</p>
                <Input
                  type="text"
                  value={city}
                  placeholder="City"
                  onChange={(e) => {
                    errorState && setErrorState(false);
                    setCity(e.target.value);
                  }}
                  className={`input w-full ${
                    errorState && errors.city && "border-red-500"
                  }`}
                />
                {errors.city && (
                  <p className="mt-1 text-xs text-red-500">{errors.city}</p>
                )}
              </div>

              <div>
                <p className="font-semibold pb-1 text-sm">State</p>
                <select
                  value={state}
                  onChange={(e) => {
                    errorState && setErrorState(false);
                    setState(e.target.value);
                  }}
                  className={`select select-bordered text-md w-full border-grey-500 bg-grey-50 dark:bg-base-200 text-base ${
                    errorState && errors.state && "select-error"
                  }`}
                >
                  {states.map((state) => (
                    <option key={state.isoCode} value={state.isoCode}>
                      {state.name}
                    </option>
                  ))}
                </select>
                {errors.state && (
                  <p className="mt-1 text-xs text-red-500">{errors.state}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="font-semibold pb-1 text-sm">ZIP Code</p>
                <Input
                  type="text"
                  value={zip}
                  placeholder="ZIP Code"
                  onChange={(e) => {
                    errorState && setErrorState(false);
                    setZip(e.target.value);
                  }}
                  className={`input w-full ${
                    errorState && errors.zip && "border-red-500"
                  }`}
                />
                {errors.zip && (
                  <p className="mt-1 text-xs text-red-500">{errors.zip}</p>
                )}
              </div>
            </div>
            <div>
              <Button className="w-full" variant="primary" type="submit">
                Sign up
              </Button>
            </div>
          </form>
          <div className="flex justify-center mt-4">
            <p className="font-semibold text-sm text-base-content/60">
              Already have an agency account?{" "}
              <Link to={appendSearchParams("/agency/login", searchParams)}>
                Login
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
